import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ProjectsMap = () => {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showLostProjects, setShowLostProjects] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    loadMapScript();
    loadProjects();
  }, []);

  // Add markers when projects are loaded
  useEffect(() => {
    if (mapInstanceRef.current && projects.length > 0 && !loading) {
      // Filter out lost projects unless explicitly shown
      const filteredProjects = showLostProjects ? 
        projects : 
        projects.filter(project => project.stage !== 'lost');
        
      console.log(`🗺️ Adding ${filteredProjects.length} markers to map (${projects.length - filteredProjects.length} lost projects hidden)`);
      addMarkersToMap(mapInstanceRef.current, filteredProjects);
    }
  }, [projects, loading, showLostProjects]);

  const loadMapScript = () => {
    if (window.google) {
      initializeMap();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = initializeMap;
    document.head.appendChild(script);
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/map/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const geocodeAllProjects = async () => {
    if (!window.confirm('Вы хотите геокодировать все проекты без координат? Это может занять некоторое время.')) {
      return;
    }

    try {
      setGeocoding(true);
      const response = await axios.post(`${API}/map/geocode-all`);
      alert(`Геокодирование завершено: ${response.data.geocoded_count} успешно, ${response.data.failed_count} неудачно`);
      loadProjects(); // Reload projects after geocoding
    } catch (error) {
      console.error('Failed to geocode projects:', error);
      alert('Не удалось выполнить геокодирование: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setGeocoding(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 48.0196, lng: 66.9237 }, // Center of Kazakhstan
      zoom: 6, // Good zoom level for Kazakhstan
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    mapInstanceRef.current = map;
    
    // Add markers when projects are loaded
    if (projects.length > 0) {
      console.log(`🗺️ Initializing map with ${projects.length} projects`);
      addMarkersToMap(map, projects);
    } else {
      console.log('⚠️ No projects available when initializing map');
    }
  };

  const addMarkersToMap = (map, projects) => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (!projects || projects.length === 0) {
      console.log('No projects to display on map');
      return;
    }

    console.log(`Adding ${projects.length} markers to map`);
    
    const bounds = new window.google.maps.LatLngBounds();
    let markersAdded = 0;

    // Group projects by coordinates to detect duplicates
    const coordinateGroups = {};
    projects.forEach(project => {
      if (!project.latitude || !project.longitude) return;
      const key = `${parseFloat(project.latitude).toFixed(4)}_${parseFloat(project.longitude).toFixed(4)}`;
      if (!coordinateGroups[key]) {
        coordinateGroups[key] = [];
      }
      coordinateGroups[key].push(project);
    });

    projects.forEach((project, index) => {
      if (!project.latitude || !project.longitude) {
        console.log(`Project ${project.name} missing coordinates`);
        return;
      }

      let lat = parseFloat(project.latitude);
      let lng = parseFloat(project.longitude);
      const key = `${lat.toFixed(4)}_${lng.toFixed(4)}`;
      const groupProjects = coordinateGroups[key];

      // If multiple projects at same location, add offset
      if (groupProjects.length > 1) {
        const projectIndex = groupProjects.indexOf(project);
        // Offset in degrees (roughly 7 km radius)
        const offsetAmount = 0.063; // ~7 km (1 degree ≈ 111 km)
        const angle = (projectIndex / groupProjects.length) * 2 * Math.PI; // Distribute in circle
        lat += Math.cos(angle) * offsetAmount * (projectIndex + 1) / groupProjects.length;
        lng += Math.sin(angle) * offsetAmount * (projectIndex + 1) / groupProjects.length;
      }

      const position = {
        lat: lat,
        lng: lng
      };

      console.log(`Adding marker ${markersAdded + 1} for ${project.name} at`, position);

      try {
        // Create marker
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: `${project.name} - ${project.city}`,
        });

        // Format budget
        const formatBudget = (amount) => {
          if (!amount) return 'Не указан';
          return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
        };

        // Format flags
        const flagLabels = {
          hot: '🔥 Горячий',
          paused: '⏸️ На паузе',
          risk: '⚠️ В зоне риска',
          waiting_construction: '🏗️ Ожидаем стройготовность',
          waiting_funding: '💰 Ждут финансирования'
        };

        const flagsHTML = project.flags && project.flags.length > 0
          ? `<div style="margin-top: 8px;">
               <strong>Флаги:</strong><br/>
               ${project.flags.map(flag => `<span style="display: inline-block; padding: 2px 8px; margin: 2px; background: #e5e7eb; border-radius: 4px; font-size: 12px;">${flagLabels[flag] || flag}</span>`).join('')}
             </div>`
          : '';

        // Create InfoWindow with budget and flags
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; max-width: 300px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${project.name}</h3>
              <p style="margin: 4px 0; color: #6b7280; font-size: 14px;">${project.city || 'Город не указан'}</p>
              <div style="margin-top: 8px;">
                <strong>Бюджет:</strong> <span style="color: #059669; font-weight: 600;">${formatBudget(project.budget_amount)}</span>
              </div>
              ${flagsHTML}
              <a href="/projects/${project.id}" style="display: inline-block; margin-top: 12px; padding: 6px 12px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;">Открыть проект</a>
            </div>
          `
        });

        // Add click listener to show InfoWindow
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        console.log(`✅ Successfully added marker ${markersAdded + 1} for ${project.name} at`, position);
        
        markersRef.current.push(marker);
        bounds.extend(position);
        markersAdded++;
        
      } catch (error) {
        console.error(`❌ Failed to create marker for ${project.name}:`, error);
      }
    });

    console.log(`🎯 Final result: ${markersAdded} markers added to map out of ${projects.length} projects`);
    console.log(`📍 First few markers coordinates:`, markersRef.current.slice(0, 3).map(m => m.getPosition().toString()));

    // Fit map to show all markers
    if (markersAdded > 0) {
      try {
        if (markersAdded === 1) {
          // For single marker, just center on it with reasonable zoom
          const position = markersRef.current[0].getPosition();
          map.setCenter(position);
          map.setZoom(10);
          console.log(`🗺️ Centered map on single marker at zoom 10`);
        } else {
          // For multiple markers, fit bounds
          map.fitBounds(bounds);
          // After bounds are set, force center on first marker for testing
          setTimeout(() => {
            const firstMarkerPos = markersRef.current[0].getPosition();
            map.setCenter(firstMarkerPos);
            map.setZoom(8);
            console.log(`🎯 FORCED CENTER on first marker:`, firstMarkerPos.toString());
          }, 1000);
        }
      } catch (error) {
        console.error('❌ Error setting map bounds:', error);
        // Fallback: center on Kazakhstan
        map.setCenter({ lat: 48.0196, lng: 66.9237 });
        map.setZoom(6);
      }
    } else {
      console.warn('⚠️ No markers were added to the map!');
      // Show all Kazakhstan anyway
      map.setCenter({ lat: 48.0196, lng: 66.9237 });
      map.setZoom(6);
    }
  };

  const createInfoWindowContent = (project) => {
    const formatCurrency = (amount, currency = 'KZT') => {
      if (!amount) return 'Не указано';
      
      if (currency === 'KZT') {
        return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
      } else if (currency === 'USD') {
        return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
      } else if (currency === 'RUB') {
        return new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';
      } else {
        return new Intl.NumberFormat('ru-RU').format(amount) + ' ' + currency;
      }
    };

    const stageLabels = {
      research: 'Исследование',
      pre_tender: 'Предтендер', 
      tender_preparation: 'Подготовка тендера',
      commercial_proposal: 'Коммерческое предложение',
      negotiations: 'Переговоры',
      won: 'Выиграно',
      lost: 'Проиграно'
    };

    return `
      <div style="max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">
          ${project.name}
        </h3>
        <div style="margin-bottom: 8px; color: #6b7280; font-size: 14px;">
          📍 ${project.city}
        </div>
        ${project.customer_company ? `
          <div style="margin-bottom: 8px; color: #6b7280; font-size: 14px;">
            🏢 ${project.customer_company}
          </div>
        ` : ''}
        <div style="margin-bottom: 8px; color: #6b7280; font-size: 14px;">
          💰 ${formatCurrency(project.budget_amount, project.budget_currency)}
        </div>
        <div style="margin-bottom: 12px;">
          <span style="background: #e5e7eb; color: #374151; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 500;">
            ${stageLabels[project.stage] || project.stage}
          </span>
        </div>
        ${project.flags && project.flags.length > 0 ? `
          <div style="margin-bottom: 12px;">
            ${project.flags.map(flag => {
              let flagColor = '#e5e7eb';
              let flagText = flag;
              if (flag === 'hot') {
                flagColor = '#fed7aa';
                flagText = '🔥 Горячий';
              } else if (flag === 'risk') {
                flagColor = '#fecaca';
                flagText = '⚠️ Риск';
              } else if (flag === 'paused') {
                flagColor = '#e5e7eb';
                flagText = '⏸️ Пауза';
              }
              return `<span style="background: ${flagColor}; color: #374151; padding: 2px 6px; border-radius: 8px; font-size: 11px; margin-right: 4px;">${flagText}</span>`;
            }).join('')}
          </div>
        ` : ''}
        <div style="text-align: center; margin-top: 12px;">
          <a href="/projects/${project.id}" style="background: #3b82f6; color: white; padding: 6px 12px; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;">
            Подробнее →
          </a>
        </div>
      </div>
    `;
  };

  // Reinitialize map when projects are loaded
  useEffect(() => {
    if (window.google && projects.length > 0) {
      console.log(`🎯 Projects loaded (${projects.length}), reinitializing map with markers`);
      initializeMap();
    }
  }, [projects]);

  const handleAddLocationClick = () => {
    if (!mapInstanceRef.current) return;

    // Add click listener to map for adding new location
    const clickListener = mapInstanceRef.current.addListener('click', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      // Remove the click listener
      window.google.maps.event.removeListener(clickListener);
      
      // Here you could open a modal to create a new project with these coordinates
      console.log('Selected coordinates:', lat, lng);
      alert(`Координаты выбраны: ${lat.toFixed(6)}, ${lng.toFixed(6)}\nДобавьте логику создания проекта здесь.`);
    });

    // Change cursor to crosshair
    mapInstanceRef.current.setOptions({ draggableCursor: 'crosshair' });
    
    alert('Кликните на карте, чтобы выбрать местоположение для нового проекта');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex items-center min-w-0">
                <img 
                  src="/profland-logo-new.jpg" 
                  alt="Profland CRM" 
                  className="h-10 w-auto mr-4 flex-shrink-0"
                />
                <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Profland CRM</h1>
              </div>
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                <Link to="/dashboard" className="nav-link" data-testid="dashboard-nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  </svg>
                  Дашборд
                </Link>
                <Link to="/kanban" className="nav-link" data-testid="kanban-nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  </svg>
                  Канбан
                </Link>
                <Link to="/projects-list" className="nav-link" data-testid="list-nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Список
                </Link>
                <Link to="/map" className="nav-link active" data-testid="map-nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Карта
                </Link>
                <Link to="/pipeline" className="nav-link" data-testid="pipeline-nav-link">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Pipeline
                </Link>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={geocodeAllProjects}
                disabled={geocoding}
                className="btn-primary mr-4"
                data-testid="geocode-all-button"
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {geocoding ? 'Геокодирование...' : 'Геокодировать все'}
              </button>
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900" data-testid="map-title">
                  Карта проектов
                </h1>
                <p className="text-gray-600 mt-2">
                  Проектов на карте: {showLostProjects ? projects.length : projects.filter(p => p.stage !== 'lost').length}
                  {!showLostProjects && projects.filter(p => p.stage === 'lost').length > 0 && (
                    <span className="text-gray-500 ml-2">
                      (скрыто проигранных: {projects.filter(p => p.stage === 'lost').length})
                    </span>
                  )}
                </p>
              </div>
              
              {/* Lost Projects Toggle */}
              <button
                onClick={() => setShowLostProjects(!showLostProjects)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showLostProjects 
                    ? 'bg-red-100 text-red-800 border-2 border-red-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
                }`}
                title={showLostProjects ? 'Скрыть проигранные проекты' : 'Показать проигранные проекты'}
              >
                {showLostProjects ? '👁️ Скрыть проигранные' : '🙈 Показать проигранные'}
              </button>
            </div>
          </div>

          {/* Map Container */}
          <div className="bg-white/80 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <div 
              ref={mapRef}
              style={{ height: '600px', width: '100%' }}
              data-testid="map-container"
            >
              {!window.google && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка карты...</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Легенда</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
                <span className="text-sm text-gray-600">Обычный проект</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-orange-500 mr-2"></div>
                <span className="text-sm text-gray-600">🔥 Горячий проект</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                <span className="text-sm text-gray-600">⚠️ Проект с риском</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-gray-500 mr-2"></div>
                <span className="text-sm text-gray-600">⏸️ Проект на паузе</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectsMap;